import { PermissionState } from '@capacitor/core';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Geolocation } from '@capacitor/geolocation';
import { LatLng } from '@/types/google/geometry';
import { getLocationFromCache, saveLocationToCache } from '@/utils/locationCache';
import { getLocationFromIpApi } from '@/utils/ipApiLocation';

export interface LocationState {
  location: LocationStateSlice;
}

interface LocationStateSlice {
  position: LatLng;
  /** User's actual GPS/device location - not affected by search location changes */
  gpsPosition: LatLng;
  loading: boolean;
  error: string | null;
  permission: PermissionState
}

const initialState: LocationStateSlice = {
  position: {
    lat: 0,
    lng: 0
  },
  gpsPosition: {
    lat: 0,
    lng: 0
  },
  loading: false,
  error: null,
  permission: 'prompt'
}

export const getLocation = createAsyncThunk(
    'location/getLocation',
    async (_, { rejectWithValue }) => {
      try {
        // Check cache first in development
        const cachedLocation = getLocationFromCache();
        if (cachedLocation) {
          return cachedLocation;
        }

        const permission = await Geolocation.checkPermissions();

        if (permission.location === 'denied') {
          console.log('Location permission denied, falling back to IP-based location');
          try {
            const ipLocation = await getLocationFromIpApi();
            saveLocationToCache(ipLocation);
            return ipLocation;
          } catch (ipError) {
            const message = 'Location permission denied and IP-based location failed. Please enable location services in your device settings.';
            console.error(message, ipError);
            return rejectWithValue(message);
          }
        }

        try {
          const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 5000 });
          const location = { lat: position.coords.latitude, lng: position.coords.longitude };
          saveLocationToCache(location);
          return location;
        } catch (error1: unknown) {
          if (error1 instanceof Error && error1.message === 'Timeout expired') {
            try {
              const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 5000 });
              const location = { lat: position.coords.latitude, lng: position.coords.longitude };
              saveLocationToCache(location);
              return location;
            } catch (error2: unknown) {
              console.log('Geolocation failed, falling back to IP-based location');
              try {
                const ipLocation = await getLocationFromIpApi();
                saveLocationToCache(ipLocation);
                return ipLocation;
              } catch (ipError) {
                console.error('Both geolocation and IP-based location failed:', error2, ipError);
                return rejectWithValue('Unable to determine your location. Please check your device settings and internet connection.');
              }
            }
          }
          
          console.log('Geolocation failed, falling back to IP-based location');
          try {
            const ipLocation = await getLocationFromIpApi();
            saveLocationToCache(ipLocation);
            return ipLocation;
          } catch (ipError) {
            console.error('Both geolocation and IP-based location failed:', error1, ipError);
            return rejectWithValue('Unable to determine your location. Please check your device settings and internet connection.');
          }
        }
      } catch (error: unknown) {
        console.error('Location detection failed:', error);
        return rejectWithValue(error instanceof Error ? error.message : 'Location detection failed');
      }
    }
);

// Create the location slice
const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    setGeometry: (state, action) => {
      state.position = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
        .addCase(getLocation.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(getLocation.fulfilled, (state, action) => {
          state.loading = false;
          state.permission = 'granted';
          state.position = action.payload;
          state.gpsPosition = action.payload;
        })
        .addCase(getLocation.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload as string;
          state.permission = 'denied';
        });
  },
});

export const locationReducer = locationSlice.reducer;
export const {
  setGeometry
} = locationSlice.actions;

export const selectPosition = (state: LocationState) => state.location.position;
/** Selector for user's actual GPS/device location - not affected by search location changes */
export const selectGpsPosition = (state: LocationState) => state.location.gpsPosition;
export const selectLocationLoading = (state: LocationState) => state.location.loading;
export const selectLocationError = (state: LocationState) => state.location.error;
export const selectLocationPermission = (state: LocationState) => state.location.permission;